import {
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  Input,
  inject,
} from '@angular/core';
import { Subject } from 'rxjs';
import { BookManagementService } from '../book-management.service';
import { Router } from '@angular/router';
import { ClipboardService } from 'ngx-clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarnDialogComponent } from '../../warn-dialog/warn-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { BmLaunchModalComponent } from '../bm-launch-modal/bm-launch-modal.component';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../services/environment.service';
import { PostTradeService } from '../../post-trade/post-trade.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { AiSummaryResponse } from '../../models/post-trade-models';
import { NgxLoadingModule } from 'ngx-loading';
import { MatIconModule } from '@angular/material/icon';
import { ReasonTextComponent } from '../../shared-components/reason-text/reason-text.component';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-policy-details',
  templateUrl: './policy-details.component.html',
  styleUrls: ['./policy-details.component.scss'],
  imports: [
    NgxLoadingModule,
    MatIconModule,
    ReasonTextComponent,
    MatTabsModule,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class PolicyDetailsComponent implements OnInit, OnDestroy {
  private bmSvc = inject(BookManagementService);
  private cdRef = inject(ChangeDetectorRef);
  private clippy = inject(ClipboardService);
  private dialog = inject(MatDialog);
  private envSvc = inject(EnvironmentService);
  private pltfrm = inject(Platform);
  private ptSvc = inject(PostTradeService);
  private ss = inject(SessionStorageService);
  private snacky = inject(MatSnackBar);
  rbs = inject(RightBridgeApiService);
  private route = inject(Router);

  @Input() policyId;
  @Input() launchButton;

  baseUrl: string;
  environment;
  loading = false;
  policyDetails = null;
  unsubscribe: Subject<any> = new Subject();
  policies = [];
  policyReview = [];
  showUpdateMessage = false;
  aiSummary;
  aiError;
  loadingAiSummary = false;
  rights = [];
  showFarData = false;
  reasonTags = {
    positive: 'Review',
    neutral: 'Information',
    negative: 'Information',
    criteria: 'score',
  };
  showPolicyReviewTab: boolean = false;
  hidePolicyHealth: boolean = false;

  ngOnInit(): void {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.getPolicyDetails();
    if (!this.launchButton) {
      this.bmSvc.getPolicyBridgeOptions().subscribe(resp => {
        if (resp['UnitSettings']) {
          const {
            LaunchButtonLabel,
            LaunchLocation,
            ShowLaunchButton,
            IncludeTransactionData,
          } = resp['UnitSettings'];

          this.showFarData = IncludeTransactionData;

          this.bmSvc.updateLaunchButtonValues({
            LaunchButtonLabel,
            LaunchLocation,
            ShowLaunchButton,
          });
        }
        this.showPolicyReviewTab =
          resp?.['UnitPMBridges']?.length &&
          resp?.['UnitPMBridges']?.find(
            bridge => bridge?.BridgeID === 'PolicyReview'
          );
        this.hidePolicyHealth =
          !resp?.['UnitPMBridges'] || resp?.['UnitPMBridges']?.length === 0;
      });
    }

    this.rights = this.ss.get('rights');
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  getPolicyDetails() {
    this.loading = true;
    this.showUpdateMessage = true;
    this.bmSvc.getPolicy(this.policyId, 'ProcessPolicy').subscribe(resp => {
      this.policyDetails = resp['policyDetails'];
      this.policies = resp['Policies'];

      if (resp['PolicyReview']) {
        this.policyReview = resp['PolicyReview'];
      }

      this.loading = false;
      this.showUpdateMessage = false;
      this.cdRef.detectChanges();
    });
  }

  getAiSummary() {
    this.loadingAiSummary = true;
    this.aiError = '';
    this.aiSummary = '';

    const requestData = {
      type: 'policy',
      profileids: this.policyId,
      promptname: 'AIPolicySummary',
      group: 'AIPolicySummary',
    };

    this.ptSvc.aiBulkProcess(requestData).subscribe({
      next: (resp: AiSummaryResponse) => {
        if (resp.result) {
          this.aiError = '';
          this.aiSummary = resp.result;
        } else {
          this.aiSummary = '';
          this.aiError = 'No data returned';
        }
        this.loadingAiSummary = false;
        this.cdRef.detectChanges();
      },
      error: err => {
        console.warn('Error: ', err);
        this.aiSummary = '';
        this.aiError = 'Error loading summary, please try again later.';
        this.loadingAiSummary = false;
      },
    });
  }

  expand(event) {
    const el = this.closestByClass(event.target, 'policy');
    el.classList.toggle('expanded');
  }

  closestByClass(el, selectClass) {
    while (!el.classList.contains(selectClass)) {
      el = el.parentNode;
      if (!el) {
        return null;
      }
    }
    return el;
  }

  launchToCase(launchApp) {
    this.loading = true;
    this.bmSvc.getPolicy(this.policyId, 'ConvertToCADA').subscribe(resp => {
      this.loading = false;
      if (resp['CurrentAnnuityDetailsArray']) {
        const launchToDetails = {
          ID: this.policyId,
          Name: resp['CurrentAnnuityDetailsArray'][0].PlanName,
        };
        this.dialog.open(BmLaunchModalComponent, {
          panelClass: 'app-bm-launch-modal',
          width: '98vw',
          autoFocus: false,
          data: {
            label: this.launchButton.LaunchButtonLabel,
            selectedAnnuities: [launchToDetails],
            launchLocation: launchApp,
          },
        });
      }
    });
  }

  nav(location) {
    this.route.navigate([`/${location}`]);
  }

  viewRawValue(value) {
    this.rbs.decryptValue(value).subscribe({
      next: resp => {
        if (resp['resultString']) {
          let dialogRef = this.dialog.open(WarnDialogComponent, {
            panelClass: 'warn-dialog',
            data: {
              headline: 'Policy Number',
              content: `Value: ${resp['resultString']}`,
              confirm: 'Copy to Clipboard',
              cancel: 'Close',
              hideCancel: false,
            },
          });
          dialogRef.afterClosed().subscribe(result => {
            if (result === 'continue') {
              this.clippy.copy(resp['resultString']);
              this.snacky.open(`Copied to clipboard!`, 'Close', {
                duration: 4000,
              });
            }
            dialogRef = null;
          });
        }
      },
      error: err => {
        console.error('ERROR: ', err);
        this.snacky.open(
          `Oops! Something went wrong. Please try again.`,
          'Close',
          {
            duration: 4000,
          }
        );
      },
    });
  }

  printPolicy() {
    this.loading = true;
    this.rbs
      .getCustomReport(
        {
          ReportType: 'template',
          UIConfig: 'policydetailsreport',
          ProfileID: this.policyId,
        },
        'pm'
      )
      .subscribe({
        next: resp => {
          const date = new Date();
          const filename =
            'AnnuityPolicyDetails-' +
            (date.getMonth() + 1) +
            date.getDate() +
            date.getFullYear() +
            '.pdf';

          const iosSafari =
            this.pltfrm.IOS ||
            (navigator?.['userAgentData']?.platform === 'macOS' &&
              navigator.maxTouchPoints > 1);

          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(resp);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });

            this.loading = false;
          };
        },
        error: () => {
          this.loading = false;
          this.snacky.open(
            `An error has occurred, please try again later.`,
            'Close',
            {
              duration: 4000,
            }
          );
        },
      });
  }

  showCard(policyRow) {
    if (
      policyRow.title === 'AI Policy Summary' &&
      !this.rights.includes('BookManagementAISummary')
    ) {
      return false;
    } else if (policyRow.title === 'Recent Transactions' && !this.showFarData) {
      return false;
    } else if (policyRow.title === 'Policy Health' && this.hidePolicyHealth) {
      return false;
    } else if (
      policyRow.title === 'Policy Review' &&
      !this.showPolicyReviewTab &&
      !this.policies.length
    ) {
      return false;
    }
    return true;
  }
}
